/**
 * Каталог стиль-паков — 19 паков (13 прежних и шесть сведения WEB-3718).
 *
 * WEB-2057: подписей паков (`name`/`description`) тут НЕТ намеренно. Это текст
 * АВТОРУ (галерея «Стиль», тулбар, тост о применении) — он живёт в
 * `stylePackLabels.ts` и берётся оттуда функцией по id пака (язык кабинета
 * известен в момент показа, WEB-1874). Причина разрыва: каталог паков тянет
 * ПУБЛИЧНАЯ страница покупателя, и вместе с подписями ей уезжал весь словарь
 * конструктора — 60 КБ несжатых в первом экране. Держится сторожем
 * `build-guards/publicEntryGuard.ts` (`findFirstScreenCabinetDictionaries`).
 *
 * Каждый пак — оцифровка живой референс-воронки: значения (радиусы, высоты,
 * трекинг, тип хрома) сняты через getComputedStyle, а не придуманы.
 * Разборы и обоснование: state/research/style-packs/teardowns/.
 *
 * Пак задаёт ФОРМУ. Цвет остаётся за палитрой (THEMES), поэтому один и тот же
 * пак в разных палитрах даёт разные, но одинаково «собранные» воронки.
 */
import type { StylePack, StylePackId } from './stylePack.types';

export const DEFAULT_STYLE_PACK_ID: StylePackId = 'soft-rounded';
/*
 * WEB-2534 — ВЕС ПЕРВОГО ЭКРАНА. Данные паков лежат по файлу на пак в
 * `stylePackData/`, весь каталог — в `stylePackCatalog.ts` (кабинет, тесты).
 * Этот файл — только РЕЕСТР: он в первом экране публичной страницы, поэтому
 * несёт один пак по умолчанию, а пак воронки приезжает лениво
 * (`loadStylePack`) — страница ждёт его вместе с надписями, ДО первого кадра
 * (`useStylePackDoor`), так что чужого или пустого оформления не бывает.
 * Отказ загрузки — пак по умолчанию, экран не падает. Кабинет кладёт весь
 * каталог в реестр на входе (`src/main.tsx`), тесты — синхронной подменой
 * (`jest.setup.ts`). Проба 29.09: 11 паков в первом экране стоили ~2,5 КБ gzip.
 */
import { retryImport } from '@/utils/lazyWithRetry';
import { pack as defaultPack } from './stylePackData/soft-rounded';

/** Список id — порядок галереи; совпадение с файлами и каталогом держит сторож WEB-2534. */
export const STYLE_PACK_IDS: readonly StylePackId[] = [
  'soft-rounded',
  'warm-cream',
  'clean-white',
  'flat-sharp',
  'cinematic-photo',
  'dark-mystic',
  'editorial-serif',
  'mono-glam',
  'bright-pop',
  'pastel-serif',
  'tech-mint',
  'product-premium',
  'blue-gradient',
  'survey-form',
  'outline-pills',
  'framed-center',
  'left-soft',
  'plain-list',
  'step-form',
];

type PackModule = { pack: StylePack };

/** Ленивый кусок на пак. Объект, а не switch: сторож сверяет ключи с файлами. */
export const STYLE_PACK_IMPORTERS: Record<StylePackId, () => Promise<PackModule>> = {
  'soft-rounded': () => import('./stylePackData/soft-rounded'),
  'warm-cream': () => import('./stylePackData/warm-cream'),
  'clean-white': () => import('./stylePackData/clean-white'),
  'flat-sharp': () => import('./stylePackData/flat-sharp'),
  'cinematic-photo': () => import('./stylePackData/cinematic-photo'),
  'dark-mystic': () => import('./stylePackData/dark-mystic'),
  'editorial-serif': () => import('./stylePackData/editorial-serif'),
  'mono-glam': () => import('./stylePackData/mono-glam'),
  'bright-pop': () => import('./stylePackData/bright-pop'),
  'pastel-serif': () => import('./stylePackData/pastel-serif'),
  'tech-mint': () => import('./stylePackData/tech-mint'),
  'product-premium': () => import('./stylePackData/product-premium'),
  'blue-gradient': () => import('./stylePackData/blue-gradient'),
  'survey-form': () => import('./stylePackData/survey-form'),
  'outline-pills': () => import('./stylePackData/outline-pills'),
  'framed-center': () => import('./stylePackData/framed-center'),
  'left-soft': () => import('./stylePackData/left-soft'),
  'plain-list': () => import('./stylePackData/plain-list'),
  'step-form': () => import('./stylePackData/step-form'),
};

const loaded = new Map<string, StylePack>([[defaultPack.id, defaultPack]]);
/** Пак, чей кусок не доехал: вместо него — пак по умолчанию. */
const failed = new Set<string>();
const inFlight = new Map<string, Promise<void>>();

function isKnownId(id: string): id is StylePackId {
  return (STYLE_PACK_IDS as readonly string[]).includes(id);
}

type SyncResolver = (id: string) => StylePack | undefined;
let syncResolver: SyncResolver | null = null;

/** Тесты: добывать пак синхронно (как куски рендерера и надписи, WEB-2054/2057). */
export function setStylePackSyncResolver(resolver: SyncResolver | null): void {
  syncResolver = resolver;
}

export function getStylePackSyncResolver(): SyncResolver | null {
  return syncResolver;
}

/** Положить пак в реестр — каталог кабинета и приехавший кусок. */
export function registerStylePack(pack: StylePack): void {
  loaded.set(pack.id, pack);
  failed.delete(pack.id);
}

/** Пак по id; undefined — если id выдуман или пак ещё не приехал. */
export function getStylePackById(id: string): StylePack | undefined {
  const known = loaded.get(id);
  if (known) return known;
  if (failed.has(id)) return defaultPack;
  const resolved = syncResolver ? syncResolver(id) : undefined;
  if (resolved) loaded.set(id, resolved);
  return resolved;
}

/** Ждать нечего: пак на руках, его кусок уже отказал, или id выдуман. */
export function hasStylePackLoaded(id: string): boolean {
  return (
    loaded.has(id) ||
    failed.has(id) ||
    !isKnownId(id) ||
    getStylePackById(id) !== undefined
  );
}

/**
 * Привезти пак воронки. Не бросает никогда: отказ куска (после одной
 * перезагрузки страницы через `retryImport`, как у кусков рендерера) — пак по
 * умолчанию. Два заказа одного пака — один запрос.
 */
export function loadStylePack(id: string): Promise<void> {
  if (hasStylePackLoaded(id) || !isKnownId(id)) return Promise.resolve();
  const pending = inFlight.get(id);
  if (pending) return pending;
  const request = retryImport<StylePack>(
    () => STYLE_PACK_IMPORTERS[id]().then((module) => ({ default: module.pack })),
    `style-pack-${id}`,
  )
    .then((module) => {
      registerStylePack(module.default);
    })
    .catch(() => {
      failed.add(id);
    })
    .finally(() => {
      inFlight.delete(id);
    });
  inFlight.set(id, request);
  return request;
}

/** Только для тестов: забыть приехавшие паки (память модуля, шрам WEB-2359). */
export function __resetStylePacksForTests(): void {
  loaded.clear();
  loaded.set(defaultPack.id, defaultPack);
  failed.clear();
  inFlight.clear();
}

/** Список валидных id — для санитайзеров и валидации на бэке. */
export const VALID_STYLE_PACK_IDS: readonly StylePackId[] = STYLE_PACK_IDS;
